import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatOdds(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return value.toFixed(2)
}

/** P0.55 (29/08/2026, Sección 6/31 del encargo): el rol interno ADMIN nunca debe aparecer
 * como si fuera un "plan" comercial dentro de Client Web -- un operador que entra a probar el
 * producto debe ver exactamente la experiencia de un cliente. ADMIN ya recibe acceso
 * equivalente a BLACK (`plan_tiers.py::resolve_features`), así que ese es el plan comercial
 * real que corresponde mostrar -- nunca la etiqueta "ADMIN" con apariencia de Dashboard. */
export function displayPlanLabel(plan: string | null | undefined): string {
  if (plan === 'ADMIN') return 'BLACK'
  return plan || 'FREE'
}

export function formatProb(value: number | null | undefined, decimals = 0): string {
  if (value === null || value === undefined) return '—'
  return `${(value * 100).toFixed(decimals)}%`
}

/** P0.32 (26/08/2026, rediseño Mini App -- Sección 1 del encargo): distingue probabilidad
 * CALIBRADA (número real específico de este pick, seguro de mostrar como "Probabilidad
 * estimada X%") de un tope de seguridad de fallback (`probability_status ===
 * 'conservative_fallback'`/'experimental', o simplemente `probability_is_calibrated ===
 * false`) -- ese número (con frecuencia exactamente 85.0%, el tope genérico de `DecisionEngine.
 * decide_and_audit`) NUNCA debe presentarse como si fuera una estimación calibrada específica.
 *
 * Sprint "Corrección Puntual -- Labs + Probabilidad Cliente" (10/09/2026, Sección 3 del
 * encargo): BUG REAL corregido -- `fallback` no llevaba `label`, así que TODO llamador que
 * distinguía únicamente `kind === 'calibrated'` (la forma obvia de leer este tipo) colapsaba
 * fallback en el mismo "No disponible"/omitido que `unknown`, aunque `value` fuera un número
 * real, no-nulo, efectivamente usado por JA para decidir/rankear (caso real reproducido:
 * "The Town vs Ventura County", `model_probability=0.85`, `probability_status=
 * 'conservative_fallback'`, `decision='recomendar'` -- el cliente veía "PROBABILIDAD JA: No
 * disponible" para un pick que John Anthony SÍ recomendó con una probabilidad real). Estar en
 * `conservative_fallback` no significa "sin Probabilidad JA" -- significa "sin calibración
 * histórica específica de ESTE mercado", así que el número real sigue siendo publicable (nunca
 * inventado, es el mismo `value` recibido). `fallback` ahora lleva su propio `label` (0
 * decimales, para no aparentar la precisión de una calibración específica) -- el llamador NUNCA
 * debe sustituirlo por la etiqueta de Confianza (dato distinto: solidez de la evidencia, no
 * probabilidad de ocurrencia) ni omitirlo en silencio. */
export type ProbabilityDisplay =
  | { kind: 'calibrated'; label: string }
  | { kind: 'fallback'; label: string }
  | { kind: 'unknown' }

export function probabilityDisplay(
  value: number | null | undefined,
  isCalibrated: boolean | null | undefined
): ProbabilityDisplay {
  if (value == null) return { kind: 'unknown' }
  if (!isCalibrated) return { kind: 'fallback', label: formatProb(value, 0) }
  return { kind: 'calibrated', label: formatProb(value, 1) }
}

/** P1.10 (25/09/2026): zona de NEGOCIO de John Anthony -- MISMA zona real (IANA, DST-aware)
 * que `app.core.time._business_timezone()` en el backend (`app_timezone`, `Europe/Madrid`).
 * Rige reglas operativas (p.ej. el run diario de las 12:00) -- Concepto B, nunca tocado en
 * este sprint. Ya NO se usa para presentar el kickoff de un partido a un usuario concreto
 * (ver `resolveDisplayTimeZone()` más abajo, Concepto C) -- ese fue exactamente el bug de
 * P1.2 (01/10/2026): un usuario en Bogotá o Los Ángeles veía cualquier kickoff convertido a
 * Madrid, no a su propia hora local real. */
export const BUSINESS_TIMEZONE = 'Europe/Madrid'

const TIMEZONE_PREFERENCE_KEY = 'ja-timezone-preference'

/** Preferencia EXPLÍCITA del usuario, si alguna vez la fijó (hoy no existe selector montado
 * en ninguna página -- infraestructura preparada, mismo patrón que `theme.ts`/`ThemeToggle`,
 * nunca se borra por no estar en uso todavía). `null` si nunca se fijó o si `localStorage`
 * no está disponible (modo privado, contexto sin storage, etc. -- nunca debe romper el
 * render, ver Requisito B del encargo). */
export function getTimeZonePreference(): string | null {
  try {
    return localStorage.getItem(TIMEZONE_PREFERENCE_KEY)
  } catch {
    return null
  }
}

export function setTimeZonePreference(tz: string): void {
  try {
    localStorage.setItem(TIMEZONE_PREFERENCE_KEY, tz)
  } catch {
    // Almacenamiento no disponible -- la preferencia simplemente no persiste, nunca rompe.
  }
}

/** P1.2 (01/10/2026, TIMEZONE GLOBAL -- Requisito B): resolutor ÚNICO de la zona horaria de
 * VISUALIZACIÓN del usuario (Concepto C) -- toda función de este módulo que muestre un
 * instante real (kickoff, timestamp de captura de cuota, etc.) pasa por AQUÍ, nunca por un
 * segundo cálculo local. Prioridad exigida por el encargo:
 *   1. Preferencia explícita del usuario (`getTimeZonePreference()`) -- cuando exista un
 *      selector real, es la fuente de verdad (p.ej. un usuario que viaja y quiere seguir
 *      viendo su zona de casa).
 *   2. `Intl.DateTimeFormat().resolvedOptions().timeZone` -- la zona IANA real que el propio
 *      motor JS del dispositivo/navegador ya resolvió (soportada en el 100% de los WebView
 *      de Telegram reales) -- NUNCA GPS, NUNCA IP, NUNCA inferida de idioma/país/nacionalidad
 *      (un usuario en EE.UU./Brasil puede estar en cualquiera de varias zonas del mismo país;
 *      el idioma "es-ES" de la UI no implica que el usuario esté en España).
 *   3. Fallback seguro y documentado si `Intl` fallara (entorno excepcional, nunca observado
 *      en producción): `'UTC'` -- nunca `BUSINESS_TIMEZONE` (eso reintroduciría exactamente
 *      el bug de P1.2: mostrar Madrid a todo el mundo). */
export function resolveDisplayTimeZone(): string {
  const preference = getTimeZonePreference()
  if (preference) return preference
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (resolved) return resolved
  } catch {
    // Intl no disponible -- cae al fallback seguro de abajo.
  }
  return 'UTC'
}

function formatInDisplayZone(
  date: Date,
  options: Intl.DateTimeFormatOptions
): string {
  return new Intl.DateTimeFormat('es-ES', { ...options, timeZone: resolveDisplayTimeZone() }).format(date)
}

/** Kickoff completo: "jue, 24 sept, 21:00" -- en la hora LOCAL REAL del usuario (Concepto C,
 * `resolveDisplayTimeZone()`), nunca en la zona de negocio de JA. `withYear` añade el año
 * cuando la fecha puede ser de otro año (p.ej. detalle de un pick histórico). El cambio de
 * fecha/mes/año resultante de la conversión de zona es CORRECTO por diseño (Sección 15 del
 * encargo) -- el mismo instante puede caer en un día de calendario distinto según la zona. */
export function formatKickoff(
  iso: string | null | undefined,
  opts?: { withYear?: boolean; withWeekday?: boolean; withMonth?: boolean }
): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return formatInDisplayZone(date, {
    weekday: opts?.withWeekday === false ? undefined : 'short',
    day: 'numeric',
    month: opts?.withMonth === false ? undefined : 'short',
    year: opts?.withYear ? 'numeric' : undefined,
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Solo la hora ("21:00"), en la zona de visualización real del usuario -- para
 * presentaciones compactas. */
export function formatKickoffTime(iso: string | null | undefined): string {
  if (!iso) return '--:--'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '--:--'
  return formatInDisplayZone(date, { hour: '2-digit', minute: '2-digit' })
}

/** Solo la fecha ("jue, 24 sept"), sin hora -- para listas/cabeceras de calendario que
 * agrupan por día en la zona de visualización real del usuario. Alias semántico de
 * `formatKickoff` sin componente de hora, para los call-sites que solo necesitan el día. */
export function formatFixtureDate(
  iso: string | null | undefined,
  opts?: { withYear?: boolean; withWeekday?: boolean }
): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return formatInDisplayZone(date, {
    weekday: opts?.withWeekday ? 'short' : undefined,
    day: 'numeric',
    month: 'short',
    year: opts?.withYear ? 'numeric' : undefined,
  })
}

/** Alias central de `formatKickoffTime` -- mismo nombre de familia que
 * `formatFixtureDate`/`formatFixtureDateTime` para los call-sites nuevos que migran desde un
 * `toLocaleTimeString()` local sin zona explícita (JA Lab/Parlay Lab/SGP Lab). */
export function formatFixtureTime(iso: string | null | undefined): string {
  return formatKickoffTime(iso)
}

/** Alias central de `formatKickoff` -- mismo nombre de familia, para los call-sites nuevos
 * que migran desde un `toLocaleString()` local sin zona explícita. */
export function formatFixtureDateTime(
  iso: string | null | undefined,
  opts?: { withYear?: boolean; withWeekday?: boolean }
): string {
  return formatKickoff(iso, opts)
}

/** Timestamp completo con segundos ("24 sept, 21:00:05") -- para eventos que no son un
 * kickoff (p.ej. cuándo se capturó una cuota en JA Lab), en la zona de visualización real
 * del usuario. Nunca reutiliza `formatKickoff` (ese omite segundos a propósito, pensado para
 * un horario de partido, no para un instante de auditoría). */
export function formatLocalTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return formatInDisplayZone(date, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/** Etiqueta relativa ("en 2 h 15 min" / "en 45 min" / "en vivo" / "finalizado") a partir de
 * la resta de instantes ABSOLUTOS (epoch ms) -- nunca una resta de horas locales en string
 * (eso rompería en el cruce de medianoche/DST y daría un valor DISTINTO según la zona del
 * usuario, violando la Sección 16 del encargo: el countdown debe verse IGUAL en cualquier
 * timezone). `now` es inyectable para tests deterministas. */
export function formatRelativeKickoff(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const diffMs = date.getTime() - now.getTime()
  if (diffMs <= 0) return diffMs > -1000 * 60 * 150 ? 'en vivo' : 'finalizado'
  const totalMinutes = Math.round(diffMs / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours <= 0) return `en ${minutes} min`
  if (minutes === 0) return `en ${hours} h`
  return `en ${hours} h ${minutes} min`
}

/** P1.10 (Sección 1/2 del encargo): estado UNIFICADO de presentación para cualquier pick --
 * la MISMA lectura en Home/Top IA/Más Seguros/Secundarios/Actividad reciente/detalle, nunca
 * una segunda derivación por componente. `fixture_status`/`settled_status` ya vienen
 * calculados por la única fuente canónica del backend (`_build_leg_detail`); esta función
 * solo traduce esa combinación al valor que espera `<StatusBadge>`. */
export function resolvePickBadgeStatus(
  fixtureStatus: string | null | undefined,
  settledStatus: string | null | undefined
): 'ganada' | 'perdida' | 'void' | 'en_juego' | 'cancelado' | 'pendiente' | null {
  if (settledStatus && settledStatus !== 'pendiente') {
    if (settledStatus === 'ganada' || settledStatus === 'perdida' || settledStatus === 'void') return settledStatus
    if (settledStatus === 'anulada') return 'void'
  }
  if (fixtureStatus === 'live') return 'en_juego'
  if (fixtureStatus === 'cancelled' || fixtureStatus === 'postponed') return 'cancelado'
  if (fixtureStatus == null) return null
  return 'pendiente'
}

/** Marcador EN VIVO real ("1-0") -- nunca fabrica un minuto (el dato no existe todavía en
 * el sistema, ver `Fixture` -- solo se muestra lo que sí es real: el marcador). `null` si
 * cualquiera de los dos goles todavía no está disponible, nunca un "0-0" inventado. */
export function liveScoreLabel(homeScore: number | null | undefined, awayScore: number | null | undefined): string | null {
  if (homeScore == null || awayScore == null) return null
  return `${homeScore}-${awayScore}`
}
